/**
 * D-EZ-101 — PLAYER PREFERENCES THAT CHANGE WHAT THE SCREEN SAYS.
 *
 * One switch lives here today: **Show rulebook citations**.
 *
 * Howard, 4 Oct 2026: rule codes are hidden from players by default, with a
 * switch to show them. Everything the engine resolves still carries its
 * citation — RB/xv, SAG/20-21, RWEG/p.7, EZRA-S2 §4.0, D-023 — and the record
 * stays auditable; the switch only decides whether a player SEES them.
 *
 *   - OFF (the default): plain racing language. No codes, no seed in the
 *     masthead, no "the engine's own words", no raw exception text.
 *   - ON: the citations show exactly as they always have (Howard's own view
 *     when he is checking a ruling against the sheets).
 *
 * Persisted per browser in localStorage. Every read and write is wrapped:
 * private windows, blocked storage and server rendering all fall back to the
 * default (OFF) and the game behaves identically apart from the switch not
 * being remembered.
 *
 * The React side is `useShowCitations()` (this file) and the `<Cite>` /
 * `<ErrorNote>` components in `components/Cite.tsx`. Non-React code (string
 * builders) can call `readShowCitations()` directly, but a component should
 * use the hook so it re-renders when the switch is flipped.
 */

import { useSyncExternalStore } from 'react';

/** localStorage key. Versioned so a future change of meaning can start clean. */
export const CITATIONS_PREF_KEY = 'rwbr.showCitations.v1';

const listeners = new Set<() => void>();

/** In-memory value, so a browser without storage still flips for the session. */
let memory: boolean | null = null;

/** Is the rulebook-citations switch ON? Default false. Never throws. */
export function readShowCitations(): boolean {
  if (memory !== null) return memory;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(CITATIONS_PREF_KEY) === 'on';
  } catch {
    return false;
  }
}

/** Flip the switch, remember it if the browser lets us, and tell every subscriber. */
export function setShowCitations(on: boolean): void {
  memory = on;
  try {
    window.localStorage.setItem(CITATIONS_PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* Nothing to remember it with — the session still has it. */
  }
  for (const fn of [...listeners]) fn();
}

/** Subscribe to changes (this tab and other tabs). Returns the unsubscribe. */
export function subscribeShowCitations(fn: () => void): () => void {
  listeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== CITATIONS_PREF_KEY) return;
    memory = null;
    fn();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(fn);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** React hook: the current value of the switch; re-renders on change. SSR/tests: false. */
export function useShowCitations(): boolean {
  // The server snapshot reads the same value: this app renders on the client
  // only, and tests (renderToStaticMarkup) can then flip the switch.
  return useSyncExternalStore(subscribeShowCitations, readShowCitations, readShowCitations);
}

/**
 * The rule-reference shapes this game prints: RB/xv, RWEG/p.4 (and "RWEG p.7"),
 * EZRA-S2 §4.0, SAG/20-21, D-023 §3, D-EZ-28, N-010, "RC trait", and bare
 * rulebook pages (p.xii).
 */
const CODE = String.raw`(?:RB\/[ivxlcIVXLC]+|RWEG\s?\/?\s?p\.\s?\d+|EZRA(?:-S2)?(?:\s?§\s?[\d.]+)?|SAG\/[\d-]+|D-EZ-\d+|D-\d{3}(?:\s?§\s?\d+)?|N-\d{3}|RC trait|p\.[ivxlc]+)`;
const PAREN = new RegExp(String.raw`\s*\([^()]*?${CODE}[^()]*\)`, 'g');
const DASHED = new RegExp(String.raw`\s*[—–]\s*${CODE}(?:[\s,·]+${CODE})*\.?`, 'g');
const LEADING = new RegExp(String.raw`^\s*${CODE}(?:\s*[,·+]\s*${CODE})*\s*[—–:]\s*`);
const BARE = new RegExp(String.raw`\s*·?\s*${CODE}(?:\s*[,·]\s*${CODE})*`, 'g');

/**
 * A string with its rulebook references taken out, for places that hold a
 * plain string rather than JSX (tooltips, built sentences). "Daytona (RB/ii)."
 * → "Daytona."; "Heat — SAG/7" → "Heat".
 */
export function stripCitations(text: string): string {
  return text
    .replace(LEADING, '')
    .replace(PAREN, '')
    .replace(DASHED, '')
    .replace(BARE, '')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Hook: a function that returns `full` with citations on, and the plain version (given or stripped) with them off. */
export function useCiteText(): (full: string, plain?: string) => string {
  const on = useShowCitations();
  return (full, plain) => (on ? full : (plain ?? stripCitations(full)));
}

/** Test seam: forget the in-memory value so the next read goes back to storage. */
export function resetPrefsForTests(): void {
  memory = null;
}

/**
 * Test seam: render something with the switch in a known position, then put it
 * back. Synchronous renders only (renderToStaticMarkup).
 */
export function withCitations<T>(on: boolean, render: () => T): T {
  const before = memory;
  memory = on;
  try {
    return render();
  } finally {
    memory = before;
  }
}

/**
 * Turn anything thrown into a sentence a player can act on, plus the raw
 * detail for the citations view. `doing` is the plain-language activity
 * ("loading the league", "settling the season").
 */
export function friendlyError(e: unknown, doing: string): { message: string; detail: string } {
  const detail = e instanceof Error ? e.message : String(e ?? '');
  const lower = detail.toLowerCase();
  let hint = 'Try again in a moment.';
  if (/network|failed to fetch|load failed|timeout|timed out|abort/.test(lower)) {
    hint = 'The connection dropped — check you are online and try again.';
  } else if (/older version|newer version|format|version/.test(lower)) {
    hint = 'That save was made by a different version of the game and cannot be opened here.';
  } else if (/sign in|unauthori[sz]ed|401|403|forbidden/.test(lower)) {
    hint = 'You may need to sign in again.';
  } else if (/quota|storage|too large|413/.test(lower)) {
    hint = 'It is too big to store — free up space or save to a file instead.';
  }
  return { message: `Something went wrong ${doing}. ${hint}`, detail };
}
